// How Cairn works — the mental model: plain files, CRDT merging, offline-
// first, one self-hosted server. Sourced from DESIGN.md and the specs, in
// user-facing words.

import { IconFileText, IconGitMerge, IconServer2, IconShieldLock } from "@tabler/icons-react";
import { DocSection, K, P, Points } from "../doc-ui";

export function ConceptsDoc() {
  return (
    <>
      <P>
        Cairn syncs Obsidian vaults through a server you run yourself. Four ideas explain
        everything else in these docs.
      </P>

      <DocSection icon={IconFileText} title="Your notes stay plain files">
        <P>
          On every device, a vault is an ordinary folder of Markdown files — Obsidian reads and
          writes them like any other vault, and any other tool can too. The plugin mirrors those
          files to sync documents behind the scenes; it never locks your content into a database
          you can't walk away with.
        </P>
      </DocSection>

      <DocSection
        icon={IconGitMerge}
        title="Concurrent edits merge — no conflict files"
        description="Each file syncs as a CRDT document, the same technology behind collaborative editors."
      >
        <Points>
          <li>
            Two people (or your own two devices) editing the same note at once get their changes
            merged character-by-character — never a <K>Note (conflicted copy).md</K>.
          </li>
          <li>
            Offline is normal, not an error: edit on a plane, and everything reconciles when you
            reconnect — even if the note changed remotely in the meantime.
          </li>
          <li>
            When a merge does combine two people's work, the plugin points it out and links to
            the note's version history, so silent merging is never silent loss — see{" "}
            <em>Version history &amp; restore</em>.
          </li>
          <li>While you're both online, you see each other's cursors live.</li>
        </Points>
      </DocSection>

      <DocSection icon={IconServer2} title="One server, three clients">
        <Points>
          <li>
            <strong>The server</strong> — a single self-hosted binary; content lives in your own
            object-storage bucket, versions and all.
          </li>
          <li>
            <strong>The Obsidian plugin</strong> — the editor-side sync engine, on desktop and
            mobile.
          </li>
          <li>
            <strong>The CLI</strong> (<K>cairn</K>) — setup and management from the terminal:
            login, clone, members, admin.
          </li>
          <li>
            <strong>This web app</strong> — read-only browsing plus management.
          </li>
          <li>
            The server also distributes its own clients: the install script, CLI binaries, and
            plugin builds all come from your server, checksum-verified — no third-party stores in
            the loop.
          </li>
        </Points>
      </DocSection>

      <DocSection icon={IconShieldLock} title="The trust model, plainly">
        <Points>
          <li>Everything travels over TLS, and every request is tied to a per-device session you
            can revoke.</li>
          <li>
            Content is <em>not</em> end-to-end encrypted: the server can read your notes (that's
            what lets it render this web view and version previews). The privacy model is
            self-hosting — you trust the machine because it's yours.
          </li>
          <li>Accounts are invite-only, with optional two-factor. See{" "}
            <em>Account &amp; security</em>.</li>
        </Points>
      </DocSection>
    </>
  );
}
